import React, { useCallback, useEffect, useRef, useState } from 'react';

interface ScrollCountUpProps {
  value: number;
  duration?: number;
  className?: string;
  suffix?: string;
  'aria-label'?: string;
}

const easeOutQuart = (progress: number) => 1 - Math.pow(1 - progress, 4);

/**
 * Reveals real vehicle numbers as the stat ledger enters the viewport.
 * The animation is deliberately local and cancellable so it never competes
 * with page scrolling or keeps a hidden page section busy.
 */
export default function ScrollCountUp({
  value,
  duration = 900,
  className,
  suffix = '',
  'aria-label': ariaLabel,
}: ScrollCountUpProps) {
  const element = useRef<HTMLSpanElement>(null);
  const frame = useRef<number | null>(null);
  const previous = useRef(0);
  const target = useRef(value);
  const lastAnimated = useRef<number | null>(null);
  const [display, setDisplay] = useState(0);
  const [entered, setEntered] = useState(false);

  const cancel = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);

  const animate = useCallback(
    (nextValue: number) => {
      cancel();
      target.current = nextValue;
      const startValue = previous.current;
      const started = performance.now();
      const step = (now: number) => {
        const progress = Math.min(1, (now - started) / duration);
        const next = startValue + (nextValue - startValue) * easeOutQuart(progress);
        previous.current = next;
        setDisplay(next);
        if (progress < 1) frame.current = requestAnimationFrame(step);
        else frame.current = null;
      };
      frame.current = requestAnimationFrame(step);
    },
    [cancel, duration]
  );

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const settle = () => {
      if (media?.matches) {
        cancel();
        previous.current = target.current;
        setDisplay(target.current);
      }
    };
    const enter = () => {
      setEntered(true);
      lastAnimated.current = target.current;
      if (media?.matches) settle();
      else animate(target.current);
    };
    if (typeof IntersectionObserver === 'undefined') {
      enter();
      return cancel;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        enter();
        observer.disconnect();
      },
      { threshold: 0.35, rootMargin: '0px 0px -8% 0px' }
    );
    if (element.current) observer.observe(element.current);
    settle();
    return () => {
      cancel();
      observer.disconnect();
    };
  }, [animate, cancel]);

  useEffect(() => {
    target.current = value;
    if (entered && lastAnimated.current !== value) {
      lastAnimated.current = value;
      const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      if (media?.matches) {
        cancel();
        previous.current = value;
        setDisplay(value);
      } else animate(value);
    }
  }, [animate, cancel, entered, value]);

  const formatted = Math.round(display).toLocaleString();
  return (
    <span
      ref={element}
      className={className}
      data-testid="scroll-count"
      data-scroll-count="true"
      data-count-target={value}
      data-count-visible={entered ? 'true' : undefined}
      aria-label={ariaLabel || `${value.toLocaleString()}${suffix}`}
    >
      {formatted}
      {suffix}
    </span>
  );
}
